import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useFeideWhitelists,
  useCreateFeideWhitelist,
  useRevokeFeideWhitelist,
  useReactivateFeideWhitelist,
  usePreviewFeideId,
  useMySystemRoles,
  useImportOrganizationsFromWhitelist,
} from "../../../api/hooks";
import { useAlert } from "../../../contexts/AlertContext";
import WhitelistDetailsModal from "./WhitelistDetailsModal";
import styles from "./SuperadminFeideWhitelist.module.css";

const SuperadminFeideWhitelist = () => {
  const { t } = useTranslation("common");
  const { alert, confirm, prompt } = useAlert();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedWhitelistId, setSelectedWhitelistId] = useState(null);

  const [newFeideId, setNewFeideId] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [autoImportChildren, setAutoImportChildren] = useState(true);
  const [autoImportParents, setAutoImportParents] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const { data: systemRolesData } = useMySystemRoles();
  const { data: whitelistsData, isLoading, refetch } = useFeideWhitelists(statusFilter, searchTerm);
  const createMutation = useCreateFeideWhitelist();
  const revokeMutation = useRevokeFeideWhitelist();
  const reactivateMutation = useReactivateFeideWhitelist();
  const previewMutation = usePreviewFeideId();
  const importOrgsMutation = useImportOrganizationsFromWhitelist();

  const systemRoles = systemRolesData?.data || [];
  const isSystemAdmin = systemRoles.includes("systemadmin");

  if (!isSystemAdmin) {
    return null;
  }

  const whitelists = whitelistsData?.data || [];

  const handlePreview = async () => {
    if (!newFeideId.trim()) {
      await alert(t('auth.settings.feideWhitelist.messages.feideIdRequired'));
      return;
    }

    try {
      const result = await previewMutation.mutateAsync({
        feideId: newFeideId,
        autoImportChildren,
        autoImportParents
      });
      setPreviewData(result.data);
    } catch (error) {
      await alert(t('auth.settings.feideWhitelist.messages.previewFailed', { error: error.message }));
    }
  };

  const handleCreate = async () => {
    if (!newFeideId.trim()) {
      await alert(t('auth.settings.feideWhitelist.messages.feideIdRequired'));
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        feideId: newFeideId,
        description: newDescription,
        autoImportChildren,
        autoImportParents
      });

      const whitelist = result.data;

      // Check if there are importable organizations
      if (previewData?.importableOrganizations && previewData.importableOrganizations.length > 0) {
        const orgNames = previewData.importableOrganizations.map(o => o.name).join(', ');
        if (await confirm(t('auth.settings.feideWhitelist.messages.importOrgsPrompt', {
          count: previewData.importableOrganizations.length,
          names: orgNames
        }))) {
          try {
            await importOrgsMutation.mutateAsync(whitelist.id);
            await alert(t('auth.settings.feideWhitelist.messages.importOrgsSuccess'));
          } catch (error) {
            await alert(t('auth.settings.feideWhitelist.messages.importOrgsFailed', { error: error.message }));
          }
        }
      } else {
        await alert(t('auth.settings.feideWhitelist.messages.createSuccess', { feideId: newFeideId }));
      }

      setShowAddForm(false);
      setNewFeideId('');
      setNewDescription('');
      setPreviewData(null);
      refetch();
    } catch (error) {
      await alert(t('auth.settings.feideWhitelist.messages.createFailed', { error: error.message }));
    }
  };

  const handleRevoke = async (id, feideId) => {
    const reason = await prompt(t('auth.settings.feideWhitelist.messages.revokePrompt', { feideId }));
    if (!reason) return;

    try {
      const result = await revokeMutation.mutateAsync({ id, reason });
      const data = result.data;
      await alert(t('auth.settings.feideWhitelist.messages.revokeSuccess', {
        feideId,
        affected: data.totalGroupsAffected,
        hidden: data.groupsHidden,
        visible: data.groupsStillVisible
      }));
      refetch();
    } catch (error) {
      await alert(t('auth.settings.feideWhitelist.messages.revokeFailed', { error: error.message }));
    }
  };

  const handleReactivate = async (id, feideId) => {
    if (!await confirm(t('auth.settings.feideWhitelist.messages.reactivateConfirm', { feideId }))) {
      return;
    }

    try {
      await reactivateMutation.mutateAsync(id);
      await alert(t('auth.settings.feideWhitelist.messages.reactivateSuccess', { feideId }));
      refetch();
    } catch (error) {
      await alert(t('auth.settings.feideWhitelist.messages.reactivateFailed', { error: error.message }));
    }
  };

  const handleRowClick = (id) => {
    setSelectedWhitelistId(id);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedWhitelistId(null);
  };

  const handleImportOrgs = async (whitelistId) => {
    try {
      const result = await importOrgsMutation.mutateAsync(whitelistId);
      const importedOrgs = result.data;
      if (importedOrgs.length > 0) {
        await alert(t('auth.settings.feideWhitelist.messages.importOrgsPrompt', {
          count: importedOrgs.length,
          names: importedOrgs.map(o => o.name).join(', ')
        }));
      } else {
        await alert(t('auth.settings.feideWhitelist.messages.noOrgsToImport'));
      }
      refetch();
    } catch (error) {
      await alert(t('auth.settings.feideWhitelist.messages.importOrgsFailed', { error: error.message }));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{t('auth.settings.feideWhitelist.title')}</h2>
        <p className={styles.description}>
          {t('auth.settings.feideWhitelist.description')}
        </p>
      </div>

      <div className={styles.controls}>
        <div className={styles.filters}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.select}
          >
            <option value="active">{t('auth.settings.feideWhitelist.filters.active')}</option>
            <option value="revoked">{t('auth.settings.feideWhitelist.filters.revoked')}</option>
            <option value="all">{t('auth.settings.feideWhitelist.filters.all')}</option>
          </select>

          <input
            type="text"
            placeholder={t('auth.settings.feideWhitelist.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.search}
          />
        </div>

        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className={styles.addButton}
        >
          {showAddForm ? t('auth.settings.feideWhitelist.cancel') : t('auth.settings.feideWhitelist.addEntry')}
        </button>
      </div>

      {showAddForm && (
        <div className={styles.addForm}>
          <h3>{t('auth.settings.feideWhitelist.addForm.title')}</h3>

          <div className={styles.formGroup}>
            <label>{t('auth.settings.feideWhitelist.addForm.feideIdLabel')}</label>
            <input
              type="text"
              value={newFeideId}
              onChange={(e) => setNewFeideId(e.target.value)}
              placeholder={t('auth.settings.feideWhitelist.addForm.feideIdPlaceholder')}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label>{t('auth.settings.feideWhitelist.addForm.descriptionLabel')}</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder={t('auth.settings.feideWhitelist.addForm.descriptionPlaceholder')}
              className={styles.textarea}
              rows={3}
            />
          </div>

          {/*<div className={styles.checkboxGroup}>*/}
          {/*  <label>*/}
          {/*    <input*/}
          {/*      type="checkbox"*/}
          {/*      checked={autoImportChildren}*/}
          {/*      onChange={(e) => setAutoImportChildren(e.target.checked)}*/}
          {/*    />*/}
          {/*    <span>{t('auth.settings.feideWhitelist.addForm.autoImportChildren')}</span>*/}
          {/*  </label>*/}

          {/*  <label>*/}
          {/*    <input*/}
          {/*      type="checkbox"*/}
          {/*      checked={autoImportParents}*/}
          {/*      onChange={(e) => setAutoImportParents(e.target.checked)}*/}
          {/*    />*/}
          {/*    <span>{t('auth.settings.feideWhitelist.addForm.autoImportParents')}</span>*/}
          {/*  </label>*/}
          {/*</div>*/}

          <div className={styles.formActions}>
            <button
              onClick={handlePreview}
              className={styles.previewButton}
              disabled={!newFeideId || previewMutation.isLoading}
            >
              {previewMutation.isLoading ? t('auth.settings.feideWhitelist.addForm.loading') : t('auth.settings.feideWhitelist.addForm.previewButton')}
            </button>

            <button
              onClick={handleCreate}
              className={styles.createButton}
              disabled={!newFeideId || createMutation.isLoading}
            >
              {createMutation.isLoading ? t('auth.settings.feideWhitelist.addForm.creating') : t('auth.settings.feideWhitelist.addForm.createButton')}
            </button>
          </div>

          {previewData && (
            <div className={styles.preview}>
              <h4>Preview Results</h4>
              {previewData.feideGroupExists ? (
                <>
                  {previewData.matchedGroups && previewData.matchedGroups.length > 1 ? (
                    <>
                      <p className={styles.multiMatch}>
                        <strong>Multiple groups matched ({previewData.matchedGroups.length}):</strong>
                      </p>
                      <details>
                        <summary>Matched groups</summary>
                        <ul>
                          {previewData.matchedGroups.map(g => (
                            <li key={g.id}>{g.name} ({g.feideId})</li>
                          ))}
                        </ul>
                      </details>
                    </>
                  ) : previewData.targetGroup ? (
                    <p><strong>Target Group:</strong> {previewData.targetGroup.name}</p>
                  ) : null}
                  <p><strong>Parents to import:</strong> {previewData.parentsToImport.length}</p>
                  <p><strong>Children to import:</strong> {previewData.childrenToImport.length}</p>
                  <p><strong>Total groups:</strong> {previewData.totalGroupsToImport}</p>

                  {previewData.parentsToImport.length > 0 && (
                    <details>
                      <summary>Parent groups ({previewData.parentsToImport.length})</summary>
                      <ul>
                        {previewData.parentsToImport.map(g => (
                          <li key={g.id}>{g.name} ({g.feideId})</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {previewData.childrenToImport.length > 0 && (
                    <details>
                      <summary>Child groups ({previewData.childrenToImport.length})</summary>
                      <ul>
                        {previewData.childrenToImport.map(g => (
                          <li key={g.id}>{g.name} ({g.feideId})</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {previewData.importableOrganizations && previewData.importableOrganizations.length > 0 && (
                    <div className={styles.importPrompt}>
                      <p className={styles.importPromptTitle}>
                        <strong>Ready to Import:</strong> {previewData.importableOrganizations.length} organization(s)
                      </p>
                      <ul>
                        {previewData.importableOrganizations.map(org => (
                          <li key={org.id}>{org.name} ({org.feideId})</li>
                        ))}
                      </ul>
                      <p className={styles.importNote}>
                        You will be prompted to import these after creating the whitelist entry.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className={styles.warning}>
                  Group not yet synced. Whitelist will be processed when a user syncs this group.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className={styles.loading}>{t('auth.settings.feideWhitelist.messages.loading')}</div>
      ) : whitelists.length === 0 ? (
        <div className={styles.empty}>
          <p>{t('auth.settings.feideWhitelist.messages.noEntries')}</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('auth.settings.feideWhitelist.table.feideId')}</th>
                <th>{t('auth.settings.feideWhitelist.table.description')}</th>
                <th>{t('auth.settings.feideWhitelist.table.status')}</th>
                <th>{t('auth.settings.feideWhitelist.table.importSettings')}</th>
                <th>{t('auth.settings.feideWhitelist.table.addedBy')}</th>
                <th>{t('auth.settings.feideWhitelist.table.date')}</th>
                <th>{t('auth.settings.feideWhitelist.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {whitelists.map((entry) => (
                <tr key={entry.id} className={styles.clickableRow}>
                  <td className={styles.feideId} onClick={() => handleRowClick(entry.id)}>{entry.feideId}</td>
                  <td className={styles.description} onClick={() => handleRowClick(entry.id)}>{entry.description || '-'}</td>
                  <td onClick={() => handleRowClick(entry.id)}>
                    <span className={entry.status === 'active' ? styles.statusActive : styles.statusRevoked}>
                      {entry.status}
                    </span>
                  </td>
                  <td className={styles.settings} onClick={() => handleRowClick(entry.id)}>
                    {entry.autoImportParents && <span className={styles.badge}>{t('auth.settings.feideWhitelist.importSettings.parents')}</span>}
                    {entry.autoImportChildren && <span className={styles.badge}>{t('auth.settings.feideWhitelist.importSettings.children')}</span>}
                  </td>
                  <td onClick={() => handleRowClick(entry.id)}>{entry.addedByUser?.fullName || 'Unknown'}</td>
                  <td onClick={() => handleRowClick(entry.id)}>{new Date(entry.createdAt).toLocaleDateString()}</td>
                  <td className={styles.actions}>
                    {entry.status === 'active' ? (
                      <>
                        <button
                          onClick={() => handleImportOrgs(entry.id)}
                          className={styles.importButton}
                          disabled={importOrgsMutation.isLoading}
                          title={t('auth.settings.feideWhitelist.messages.importOrgsTitle')}
                        >
                          {t('auth.settings.feideWhitelist.actions.importOrgs')}
                        </button>
                        <button
                          onClick={() => handleRevoke(entry.id, entry.feideId)}
                          className={styles.revokeButton}
                          disabled={revokeMutation.isLoading}
                        >
                          {t('auth.settings.feideWhitelist.actions.revoke')}
                        </button>
                      </>
                    ) : entry.status === 'revoked' ? (
                      <button
                        onClick={() => handleReactivate(entry.id, entry.feideId)}
                        className={styles.reactivateButton}
                        disabled={reactivateMutation.isLoading}
                      >
                        {t('auth.settings.feideWhitelist.actions.reactivate')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WhitelistDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseModal}
        whitelistId={selectedWhitelistId}
      />
    </div>
  );
};

export default SuperadminFeideWhitelist;
